import { useContext } from 'react';
import Box from '@mui/material/Box';
import { ReactComponent as DownloadIcon } from '../../assets/icons/Download.svg';
import { IconButton, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { COMPONENT_UNKNOWN_FAILURE, FEEDBACK_ACTION_FAILURE } from '../../util/global_variables';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ExportDataGridRow } from './data_grid/ExportDataGrid';

const SOURCE = 'ExportCompletedNotification.tsx';

interface ExportCompletedNotificationProps {
  latest_completed_row: ExportDataGridRow | null;
  handleExportDownload: (row: ExportDataGridRow | null) => void;
}

const ExportCompletedNotification = ({
  latest_completed_row,
  handleExportDownload
}: ExportCompletedNotificationProps) => {
  const theme: Theme = useTheme();
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        width: 'auto',
        maxWidth: 350,
        padding: 2,
        borderRadius: 2,
        bgcolor: 'light-grey',
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: theme.shadows[1]
      }}
    >
      <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.primary.dark,
          fontWeight: 500,
          margin: 0
        }}
      >
        Your export is ready for download!
      </Typography>
      <Tooltip title="Download Export">
        <IconButton
          onClick={async () => {
            try {
              await handleExportDownload(latest_completed_row);
            } catch (err: unknown) {
              const tracking_id: string = generateUuidV4();
              log(
                'error',
                new MetadataError(
                  err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
                  null,
                  tracking_id
                )
              );
              setFeedbackData({
                message: FEEDBACK_ACTION_FAILURE('Unable to download export', tracking_id),
                state: true,
                type: 'error'
              });
            }
          }}
          sx={{
            bgcolor: 'white',
            '&:hover': {
              bgcolor: theme.palette.grey[100]
            }
          }}
        >
          <DownloadIcon width={25} style={{ color: theme.palette.primary.main }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ExportCompletedNotification;
